import React from 'react';
import {
  Field,
  useField,
} from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';
import useClasses from '../../../hooks/useClasses';

import './style.scss';

interface Props {
  title: string
  fieldName: string
  min?: string
  readonly?: boolean
  onChange?: (value: string) => void;
}

const DateStepperField: React.FC<Props> = ({
  title,
  fieldName,
  min,
  readonly,
  onChange,
}) => {
  const { buildClass } = useClasses('DateStepper');
  const formik = useExtendedFormikContext();

  const field = useField(fieldName);

  return (
    <div className={buildClass()}>
      <div className={buildClass('container')}>
        <label className={buildClass('label')} htmlFor={fieldName}>
          {title}
        </label>

        {formik.error && formik.touched && (
          <span>{formik.error}</span>
        )}

        <Field
          {...field}
        >
          {() => (
            <DateStepperInput
              fieldName={fieldName}
              onChange={(newValue) => {
                if (!readonly) {
                  formik.setFieldValue(fieldName, newValue);
                }

                if (onChange) {
                  onChange(newValue);
                }
              }}
              min={min}
              value={formik.values[fieldName]}
              readonly={readonly}
            />
          )}
        </Field>
      </div>
    </div>
  );
};

interface DateStepperInputProps {
  fieldName: string
  value: string
  onChange: (value: string) => void
  min?: string
  readonly?: boolean
}

function DateStepperInput({
  fieldName,
  value,
  onChange,
  min,
  readonly,
}: DateStepperInputProps) {
  const { t } = useTranslation();

  const selectedDate = value && moment(value);
  const { buildClass, joinClasses } = useClasses('DateStepper');

  const today = moment();

  const formatDateValue = (date: moment.Moment) => date.format('YYYY-MM-DD');

  const setValue = (date: moment.Moment) => {
    onChange(formatDateValue(date));
  };

  const formatDateDisplay = (date: moment.Moment | string) => {
    const dateToDisplay = typeof date === 'string' ? moment(date) : date;
    const differenceInDays = today.diff(dateToDisplay, 'day');

    switch (differenceInDays) {
      case 0:
        return t('dates.today');
      case 1:
        return t('dates.yesterday');
      default:
        return dateToDisplay.format('dddd D MMM');
    }
  };

  const isMinimum = min && selectedDate && selectedDate.isSameOrBefore(moment(min), 'day');
  const isMaximum = selectedDate && selectedDate.isSameOrAfter(today, 'day');

  const decrement = () => {
    if (selectedDate && !isMinimum) {
      const newDate = selectedDate.subtract(1, 'day');
      setValue(newDate);
      return;
    }

    if (!isMinimum) {
      setValue(today);
    }
  };

  const increment = () => {
    if (selectedDate && !isMaximum) {
      const newDate = selectedDate.add(1, 'day');
      setValue(newDate);
      return;
    }

    setValue(today);
  };

  return (
    <div className={buildClass('controls')}>
      {!readonly && (
        <button
          className={joinClasses(buildClass('control', 'decrement'), 'icon-after')}
          type="button"
          onClick={decrement}
          disabled={isMinimum}
        >
          <span className="sr-only">
            {t('pages.daily_registration.buttons.earlier')}
          </span>
        </button>
      )}

      <div className={buildClass('selected-date')}>
        {value && formatDateDisplay(value)}
      </div>
      <input
        className={buildClass('input')}
        id={fieldName}
        name={fieldName}
        value={value}
        type="hidden"
        onChange={(e) => onChange(e.target.value)}
      />

      {!readonly && (
        <button
          className={joinClasses(buildClass('control', 'increment'), 'icon-after')}
          type="button"
          onClick={increment}
          disabled={isMaximum}
        >
          <span className="sr-only">
            {t('pages.daily_registration.buttons.later')}
          </span>
        </button>
      )}
    </div>
  );
}

export default DateStepperField;
